import React from "react";
import { Container, Typography, Box, Divider } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";

interface Section {
  heading: string;
  content: (string | string[])[];
}

const DE_SECTIONS: Section[] = [
  {
    heading: "§ 1 Geltungsbereich",
    content: [
      [
        "1. Diese Allgemeinen Geschäftsbedingungen gelten für alle Verträge zwischen der TOP Buchhaltungsservice UG (haftungsbeschränkt), Stuttgartstraße 15–17, 44143 Dortmund, Deutschland, nachfolgend \u201ETOP BHS\u201C, und ihren Kunden über die Erbringung von buchhalterischen, lohnbuchhalterischen sowie sonstigen kaufmännischen und betriebswirtschaftlichen Dienstleistungen.",
        "2. Diese AGB gelten ausschließlich gegenüber Unternehmern im Sinne des § 14 BGB, juristischen Personen des öffentlichen Rechts und öffentlich-rechtlichen Sondervermögen.",
        "3. Abweichende, entgegenstehende oder ergänzende Bedingungen des Kunden werden nur dann Vertragsbestandteil, wenn TOP BHS ihrer Geltung ausdrücklich in Textform zugestimmt hat.",
      ],
    ],
  },
  {
    heading: "§ 2 Leistungen",
    content: [
      [
        "1. Der konkrete Leistungsumfang ergibt sich aus dem jeweiligen Angebot, der Auftragsbestätigung oder einer sonstigen individuellen Vereinbarung in Textform.",
        "2. TOP BHS erbringt – soweit gesetzlich zulässig – insbesondere Leistungen im Bereich der laufenden Finanzbuchhaltung, der laufenden Lohn- und Gehaltsabrechnung, der Fertigung laufender Lohnsteuer-Anmeldungen sowie sonstige kaufmännische und betriebswirtschaftliche Unterstützungsleistungen.",
        "3. Steuer- und Rechtsberatung sind nicht Gegenstand des Vertrags, soweit sie nicht ausdrücklich und rechtlich zulässig gesondert vereinbart wurden.",
        "4. TOP BHS ist berechtigt, zur Leistungserbringung geeignete Dritte einzusetzen.",
      ],
    ],
  },
  {
    heading: "§ 3 Vertragsschluss",
    content: [
      [
        "1. Angebote von TOP BHS sind freibleibend und unverbindlich, sofern sie nicht ausdrücklich als verbindlich bezeichnet sind.",
        "2. Ein Vertrag kommt durch Annahme des Angebots durch den Kunden und Bestätigung durch TOP BHS in Textform oder durch Beginn der Leistungserbringung zustande.",
      ],
    ],
  },
  {
    heading: "§ 4 Mitwirkungspflichten des Kunden",
    content: [
      [
        "1. Der Kunde stellt TOP BHS alle für die Leistungserbringung erforderlichen Unterlagen, Informationen und Belege vollständig, richtig und rechtzeitig zur Verfügung.",
        "2. Der Kunde ist dafür verantwortlich, dass die übergebenen Daten und Unterlagen inhaltlich zutreffend und vollständig sind.",
        "3. Verzögerungen, die auf verspäteter, unvollständiger oder fehlerhafter Mitwirkung des Kunden beruhen, gehen nicht zu Lasten von TOP BHS.",
      ],
    ],
  },
  {
    heading: "§ 5 Vergütung und Zahlungsbedingungen",
    content: [
      [
        "1. Es gelten die im jeweiligen Angebot oder in der Auftragsbestätigung vereinbarten Preise.",
        "2. Alle Preise verstehen sich netto zuzüglich der jeweils geltenden gesetzlichen Umsatzsteuer.",
        "3. Rechnungen sind innerhalb von 14 Tagen ab Rechnungsdatum ohne Abzug zur Zahlung fällig, sofern nichts anderes vereinbart ist.",
        "4. Bei Zahlungsverzug ist TOP BHS berechtigt, die gesetzlichen Verzugszinsen sowie weitere gesetzliche Verzugsschäden geltend zu machen.",
      ],
    ],
  },
  {
    heading: "§ 6 Leistungsfristen",
    content: [
      [
        "1. Leistungsfristen und Termine sind nur verbindlich, wenn sie ausdrücklich als verbindlich vereinbart wurden.",
        "2. Soweit für die Leistungserbringung Mitwirkungshandlungen des Kunden erforderlich sind, beginnen Fristen nicht vor vollständigem Eingang der erforderlichen Unterlagen und Informationen.",
      ],
    ],
  },
  {
    heading: "§ 7 Vertraulichkeit",
    content: [
      [
        "1. Beide Vertragsparteien verpflichten sich, alle ihnen im Zusammenhang mit dem Vertragsverhältnis bekannt gewordenen vertraulichen Informationen geheim zu halten und nur für die Zwecke des Vertrags zu verwenden.",
        "2. Dies gilt nicht für Informationen, die allgemein bekannt sind, ohne Verstoß gegen diese Verpflichtung bekannt werden oder aufgrund gesetzlicher Vorschriften offengelegt werden müssen.",
      ],
    ],
  },
  {
    heading: "§ 8 Haftung",
    content: [
      [
        "1. TOP BHS haftet unbeschränkt für Schäden aus Vorsatz und grober Fahrlässigkeit.",
        "2. TOP BHS haftet ferner unbeschränkt für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit.",
        "3. Bei einfacher Fahrlässigkeit haftet TOP BHS nur bei Verletzung wesentlicher Vertragspflichten; in diesem Fall ist die Haftung auf den vertragstypischen, vorhersehbaren Schaden begrenzt.",
        "4. Die Haftung nach zwingenden gesetzlichen Vorschriften bleibt unberührt.",
      ],
    ],
  },
  {
    heading: "§ 9 Datenschutz",
    content: [
      "TOP BHS verarbeitet personenbezogene Daten im Rahmen der gesetzlichen Datenschutzvorschriften. Weitere Informationen ergeben sich aus der Datenschutzerklärung auf der Website.",
    ],
  },
  {
    heading: "§ 10 Schlussbestimmungen",
    content: [
      [
        "1. Es gilt das Recht der Bundesrepublik Deutschland.",
        "2. Gerichtsstand für alle Streitigkeiten aus oder im Zusammenhang mit dem Vertragsverhältnis ist – soweit gesetzlich zulässig – Dortmund.",
        "3. Sollten einzelne Bestimmungen dieser AGB ganz oder teilweise unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.",
      ],
    ],
  },
];

const EN_SECTIONS: Section[] = [
  {
    heading: "§ 1 Scope",
    content: [
      [
        "1. These General Terms and Conditions apply to all contracts between TOP Buchhaltungsservice UG (haftungsbeschränkt), Stuttgartstraße 15–17, 44143 Dortmund, Germany, hereinafter \"TOP BHS\", and its clients for the provision of bookkeeping, payroll and other commercial and business services.",
        "2. These T&Cs apply exclusively to entrepreneurs within the meaning of § 14 BGB, legal entities under public law and special funds under public law.",
        "3. Deviating, conflicting or supplementary terms of the client only become part of the contract if TOP BHS has expressly agreed to their validity in text form.",
      ],
    ],
  },
  {
    heading: "§ 2 Services",
    content: [
      [
        "1. The specific scope of services is determined by the respective offer, order confirmation or other individual agreement in text form.",
        "2. TOP BHS provides – to the extent permitted by law – services in particular in the areas of ongoing financial accounting, ongoing payroll processing, preparation of payroll tax returns, and other commercial and business support services.",
        "3. Tax and legal advice are not part of the contract unless expressly and legally separately agreed.",
        "4. TOP BHS is entitled to engage suitable third parties to provide the services.",
      ],
    ],
  },
  {
    heading: "§ 3 Conclusion of Contract",
    content: [
      [
        "1. Offers from TOP BHS are non-binding and subject to change unless expressly designated as binding.",
        "2. A contract is concluded upon acceptance of the offer by the client and confirmation by TOP BHS in text form, or upon commencement of service delivery.",
      ],
    ],
  },
  {
    heading: "§ 4 Client Cooperation Obligations",
    content: [
      [
        "1. The client shall provide TOP BHS with all documents, information and records required for service delivery completely, correctly and in a timely manner.",
        "2. The client is responsible for ensuring that the data and documents provided are accurate and complete.",
        "3. Delays attributable to late, incomplete or incorrect cooperation by the client shall not be borne by TOP BHS.",
      ],
    ],
  },
  {
    heading: "§ 5 Remuneration and Payment Terms",
    content: [
      [
        "1. The prices agreed in the respective offer or order confirmation shall apply.",
        "2. All prices are net plus the applicable statutory value added tax.",
        "3. Invoices are due for payment within 14 days of the invoice date without deduction, unless otherwise agreed.",
        "4. In the event of late payment, TOP BHS is entitled to claim statutory default interest and further statutory damages.",
      ],
    ],
  },
  {
    heading: "§ 6 Performance Deadlines",
    content: [
      [
        "1. Performance deadlines and dates are only binding if expressly agreed as binding.",
        "2. Where the provision of services requires cooperation by the client, deadlines do not commence until all required documents and information have been received in full.",
      ],
    ],
  },
  {
    heading: "§ 7 Confidentiality",
    content: [
      [
        "1. Both contracting parties undertake to keep confidential all confidential information that has come to their knowledge in connection with the contractual relationship and to use it only for the purposes of the contract.",
        "2. This does not apply to information that is generally known, becomes known without breach of this obligation, or must be disclosed by law.",
      ],
    ],
  },
  {
    heading: "§ 8 Liability",
    content: [
      [
        "1. TOP BHS is liable without limitation for damages arising from intent and gross negligence.",
        "2. TOP BHS is also liable without limitation for damages arising from injury to life, body or health.",
        "3. In cases of simple negligence, TOP BHS is only liable for breach of material contractual obligations; in this case liability is limited to foreseeable, contract-typical damage.",
        "4. Liability under mandatory statutory provisions remains unaffected.",
      ],
    ],
  },
  {
    heading: "§ 9 Data Protection",
    content: [
      "TOP BHS processes personal data in accordance with statutory data protection regulations. Further information is available in the privacy policy on the website.",
    ],
  },
  {
    heading: "§ 10 Final Provisions",
    content: [
      [
        "1. The law of the Federal Republic of Germany applies.",
        "2. The place of jurisdiction for all disputes arising from or in connection with the contractual relationship is – to the extent permitted by law – Dortmund.",
        "3. Should individual provisions of these T&Cs be or become wholly or partially invalid, the validity of the remaining provisions shall not be affected.",
      ],
    ],
  },
];

const AR_SECTIONS: Section[] = [
  {
    heading: "§ 1 نطاق التطبيق",
    content: [
      [
        "1. تسري هذه الشروط والأحكام العامة على جميع العقود بين TOP Buchhaltungsservice UG (haftungsbeschränkt)، Stuttgartstraße 15–17، 44143 Dortmund، ألمانيا، يُشار إليها فيما يلي بـ«TOP BHS»، وعملائها فيما يخص تقديم خدمات المحاسبة وكشوف الرواتب والخدمات التجارية والاقتصادية الأخرى.",
        "2. تسري هذه الشروط حصراً على رجال الأعمال بموجب § 14 BGB والأشخاص الاعتباريين العامين والأموال العامة الخاصة.",
        "3. لا تصبح الشروط المخالفة أو المتعارضة أو التكميلية للعميل جزءاً من العقد إلا إذا وافقت TOP BHS صراحةً على سريانها كتابةً.",
      ],
    ],
  },
  {
    heading: "§ 2 الخدمات",
    content: [
      [
        "1. يتحدد النطاق المحدد للخدمات بموجب العرض أو تأكيد الطلب أو أي اتفاقية فردية أخرى كتابةً.",
        "2. تقدم TOP BHS – بقدر ما يسمح به القانون – خدمات في مجالات المحاسبة المالية الجارية وإعداد كشوف الرواتب وإقرارات ضريبة الرواتب وخدمات الدعم التجاري والاقتصادي الأخرى.",
        "3. لا تشمل المشورة الضريبية والقانونية موضوع العقد ما لم يُتفق عليها صراحةً وبشكل مشروع بشكل منفصل.",
        "4. يحق لـ TOP BHS الاستعانة بأطراف ثالثة مؤهلة لتقديم الخدمات.",
      ],
    ],
  },
  {
    heading: "§ 3 إبرام العقد",
    content: [
      [
        "1. عروض TOP BHS غير ملزمة وغير مُلزِمة ما لم تُصنَّف صراحةً على أنها ملزمة.",
        "2. يُبرم العقد بقبول العميل للعرض وتأكيد TOP BHS كتابةً أو بالبدء في تقديم الخدمة.",
      ],
    ],
  },
  {
    heading: "§ 4 التزامات تعاون العميل",
    content: [
      [
        "1. يلتزم العميل بتزويد TOP BHS بجميع المستندات والمعلومات والإيصالات اللازمة لتقديم الخدمة بشكل كامل وصحيح وفي الوقت المناسب.",
        "2. العميل مسؤول عن دقة واكتمال البيانات والمستندات المقدمة.",
        "3. التأخيرات الناجمة عن تعاون العميل المتأخر أو المنقوص أو الخاطئ لا تقع على عاتق TOP BHS.",
      ],
    ],
  },
  {
    heading: "§ 5 الأتعاب وشروط الدفع",
    content: [
      [
        "1. تسري الأسعار المتفق عليها في العرض أو تأكيد الطلب.",
        "2. جميع الأسعار صافية زائد ضريبة القيمة المضافة القانونية السارية.",
        "3. تستحق الفواتير السداد خلال 14 يوماً من تاريخ الفاتورة دون خصم ما لم يُتفق على خلاف ذلك.",
        "4. في حالة التأخر في السداد، يحق لـ TOP BHS المطالبة بفوائد التأخير القانونية وأي أضرار تأخير قانونية أخرى.",
      ],
    ],
  },
  {
    heading: "§ 6 مواعيد تقديم الخدمة",
    content: [
      [
        "1. مواعيد وتواريخ تقديم الخدمة ملزمة فقط إذا اتُّفق صراحةً على إلزاميتها.",
        "2. حيثما يتطلب تقديم الخدمة تعاون العميل، لا تبدأ المواعيد إلا بعد استلام جميع المستندات والمعلومات المطلوبة بالكامل.",
      ],
    ],
  },
  {
    heading: "§ 7 السرية",
    content: [
      [
        "1. يلتزم كلا الطرفين بالحفاظ على سرية جميع المعلومات السرية التي اطلعا عليها في سياق العلاقة التعاقدية واستخدامها فقط لأغراض العقد.",
        "2. لا ينطبق ذلك على المعلومات المتاحة للعموم أو التي تصبح متاحة دون انتهاك هذا الالتزام أو التي يجب الإفصاح عنها بموجب القانون.",
      ],
    ],
  },
  {
    heading: "§ 8 المسؤولية",
    content: [
      [
        "1. تتحمل TOP BHS المسؤولية الكاملة عن الأضرار الناجمة عن التعمد والإهمال الجسيم.",
        "2. تتحمل TOP BHS أيضاً المسؤولية الكاملة عن الأضرار الناجمة عن الإخلال بالحياة أو الجسد أو الصحة.",
        "3. في حالات الإهمال البسيط، تكون مسؤولية TOP BHS مقتصرة على انتهاك الالتزامات التعاقدية الجوهرية؛ وفي هذه الحالة تُحدد المسؤولية بالضرر التعاقدي المتوقع.",
        "4. تبقى المسؤولية بموجب الأحكام القانونية الإلزامية غير متأثرة.",
      ],
    ],
  },
  {
    heading: "§ 9 حماية البيانات",
    content: [
      "تعالج TOP BHS البيانات الشخصية وفق أحكام حماية البيانات القانونية. مزيد من المعلومات متاح في سياسة الخصوصية على الموقع.",
    ],
  },
  {
    heading: "§ 10 أحكام ختامية",
    content: [
      [
        "1. يسري قانون جمهورية ألمانيا الاتحادية.",
        "2. مكان الاختصاص القضائي لجميع النزاعات الناشئة عن العلاقة التعاقدية أو المتعلقة بها هو – بقدر ما يسمح به القانون – دورتموند.",
        "3. إذا كانت أحكام فردية من هذه الشروط باطلة كلياً أو جزئياً أو أصبحت كذلك، فإن صحة الأحكام المتبقية تبقى غير متأثرة.",
      ],
    ],
  },
];

const AgbPage: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const isAr = currentLanguage === "ar";

  const sections =
    currentLanguage === "ar" ? AR_SECTIONS : currentLanguage === "en" ? EN_SECTIONS : DE_SECTIONS;

  const title =
    currentLanguage === "ar"
      ? "الشروط والأحكام العامة (B2B)"
      : currentLanguage === "en"
        ? "General Terms and Conditions (B2B)"
        : "Allgemeine Geschäftsbedingungen (B2B)";

  const overline =
    currentLanguage === "ar" ? "قانوني" : currentLanguage === "en" ? "Legal" : "Rechtliches";

  return (
    <Container maxWidth="md" sx={{ py: 10, dir: isAr ? "rtl" : "ltr" }}>
      <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 3, display: "block", mb: 2 }}>
        {overline}
      </Typography>
      <Typography variant="h2" sx={{ mb: 6 }}>
        {title}
      </Typography>

      {sections.map((section, idx) => (
        <Box key={idx} sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: "#1a2640" }}>
            {section.heading}
          </Typography>
          {section.content.map((block, bi) =>
            Array.isArray(block) ? (
              <Box key={bi} sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
                {block.map((line, li) => (
                  <Typography key={li} variant="body1" sx={{ color: "text.secondary", lineHeight: 1.85 }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography key={bi} variant="body1" sx={{ color: "text.secondary", lineHeight: 1.85, mb: 1 }}>
                {block}
              </Typography>
            )
          )}
          {idx < sections.length - 1 && <Divider sx={{ mt: 4 }} />}
        </Box>
      ))}
    </Container>
  );
};

export default AgbPage;
